import { getModelTagsConfig, ModelTagConfig } from '../services/tauri-api';

// タグ設定のキャッシュ（初回取得時に保存）
let cachedTags: ModelTagConfig | null = null;
let tagsLoading: Promise<ModelTagConfig> | null = null;
// モデル名ごとのタグ設定キャッシュ
const modelTagsCache: Map<string, ModelTagConfig> = new Map();

/**
 * タグ設定を取得（キャッシュを使用）
 * @param modelName モデル名（オプション）
 */
export async function getTagsConfigForModel(modelName?: string | null): Promise<ModelTagConfig> {
  return getTagsConfig(modelName);
}

/**
 * タグ設定を取得（キャッシュを使用）
 * @param modelName モデル名（オプション）
 */
async function getTagsConfig(modelName?: string | null): Promise<ModelTagConfig> {
  // モデル名が指定されている場合
  if (modelName) {
    // キャッシュを確認
    if (modelTagsCache.has(modelName)) {
      return modelTagsCache.get(modelName)!;
    }
    
    // モデル名を指定してタグ設定を取得
    try {
      const response = await getModelTagsConfig(modelName);
      if (response.success && response.data) {
        modelTagsCache.set(modelName, response.data);
        return response.data;
      }
    } catch (error) {
      console.error(`モデル ${modelName} のタグ設定の取得に失敗:`, error);
    }
  }
  
  // デフォルトのタグ設定を取得
  if (cachedTags) {
    return cachedTags;
  }
  
  // 既にロード中の場合は待機
  if (tagsLoading) {
    return tagsLoading;
  }
  
  // ロードを開始
  tagsLoading = (async () => {
    try {
      const response = await getModelTagsConfig();
      if (response.success && response.data) {
        cachedTags = response.data;
        return cachedTags;
      }
    } catch (error) {
      console.error('タグ設定の取得に失敗:', error);
    }
    
    // フォールバック: デフォルトのLlama形式タグ
    const defaultTags: ModelTagConfig = {
      system: {
        start: '<|start_header_id|>system<|end_header_id|>',
        end: '<|eot_id|>'
      },
      user: {
        start: '<|start_header_id|>user<|end_header_id|>',
        end: '<|eot_id|>'
      },
      assistant: {
        start: '<|start_header_id|>assistant<|end_header_id|>',
        end: '<|eot_id|>'
      },
      bos_token: '<|begin_of_text|>',
      system_message: 'あなたは親切で役に立つAIアシスタントです。'
    };
    
    if (!cachedTags) {
      cachedTags = defaultTags;
    }
    
    return cachedTags;
  })();
  
  return tagsLoading;
}

/**
 * タグ設定を初期化（アプリ起動時に呼び出す）
 */
export async function initializeTagsConfig(): Promise<void> {
  await getTagsConfig();
}

/**
 * タグ設定のキャッシュをクリア（設定変更時に呼び出す）
 */
export function clearTagsCache(): void {
  cachedTags = null;
  tagsLoading = null;
  modelTagsCache.clear();
}

/**
 * プロンプト文字列から、最後のユーザーメッセージを抽出
 * モデルタグ設定を使用して動的にパース
 * 
 * @param prompt プロンプト文字列
 * @returns 最後のユーザーメッセージの内容（空行をトリム）
 */
export async function extractLastUserMessage(prompt: string): Promise<string> {
  if (!prompt || prompt.trim() === '') {
    return '';
  }

  try {
    const tags = await getTagsConfig();
    const userHeader = tags.user.start;
    const userEnd = tags.user.end;
    
    // 最後のユーザーヘッダーを探す
    const lastUserHeaderIndex = prompt.lastIndexOf(userHeader);
    
    if (lastUserHeaderIndex === -1) {
      // ヘッダーが見つからない場合、元の文字列を返す（後方互換性）
      return prompt;
    }

    // ヘッダーの後の位置を取得
    const afterHeaderIndex = lastUserHeaderIndex + userHeader.length;
    
    // その後の終了タグを探す
    const endIndex = prompt.indexOf(userEnd, afterHeaderIndex);
    
    if (endIndex === -1) {
      // 終了タグが見つからない場合、ヘッダー以降のすべてを返す
      return prompt.substring(afterHeaderIndex).trim();
    }

    // ヘッダーと終了タグの間の内容を抽出
    const content = prompt.substring(afterHeaderIndex, endIndex).trim();
    
    return content;
  } catch (error) {
    console.error('タグ設定の取得に失敗しました:', error);
    // エラー時は後方互換性のため、元の文字列を返す
    return prompt;
  }
}

/**
 * 同期的なバージョン（後方互換性のため）
 * キャッシュされたタグ設定を使用するか、デフォルトタグを使用
 * @param prompt プロンプト文字列
 * @param modelName モデル名（オプション、チャットのmodel属性から取得）
 */
export function extractLastUserMessageSync(prompt: string, modelName?: string | null): string {
  if (!prompt || prompt.trim() === '') {
    return '';
  }

  // モデル名が指定されている場合は、そのモデルのキャッシュされたタグ設定を使用
  let tags: ModelTagConfig;
  if (modelName && modelTagsCache.has(modelName)) {
    tags = modelTagsCache.get(modelName)!;
  } else {
    // キャッシュされたタグがあれば使用、なければデフォルト
    tags = cachedTags || {
      system: {
        start: '<|start_header_id|>system<|end_header_id|>',
        end: '<|eot_id|>'
      },
      user: {
        start: '<|start_header_id|>user<|end_header_id|>',
        end: '<|eot_id|>'
      },
      assistant: {
        start: '<|start_header_id|>assistant<|end_header_id|>',
        end: '<|eot_id|>'
      },
      bos_token: '<|begin_of_text|>',
      system_message: 'あなたは親切で役に立つAIアシスタントです。'
    };
  }
  
  const userHeader = tags.user.start;
  const userEnd = tags.user.end;
  
  // 最後のユーザーヘッダーを探す
  const lastUserHeaderIndex = prompt.lastIndexOf(userHeader);
  
  if (lastUserHeaderIndex === -1) {
    // ヘッダーが見つからない場合、元の文字列を返す（後方互換性）
    return prompt;
  }

  // ヘッダーの後の位置を取得
  const afterHeaderIndex = lastUserHeaderIndex + userHeader.length;
  
  // その後の終了タグを探す
  const endIndex = prompt.indexOf(userEnd, afterHeaderIndex);
  
  if (endIndex === -1) {
    // 終了タグが見つからない場合、ヘッダー以降のすべてを返す
    return prompt.substring(afterHeaderIndex).trim();
  }

  // ヘッダーと終了タグの間の内容を抽出
  const content = prompt.substring(afterHeaderIndex, endIndex).trim();
  
  return content;
}

